<script setup lang="ts">
  import { Picture, Button } from '@travelata-libs/ui-kit';
  import { useViewport, Breakpoint } from '@/shared/composables';
  import { HeaderAccount } from '@/features/header-account';
  import type { AccountUser } from '@/features/header-account';
  import Teleport from 'vue2-teleport';
  import HeaderNav from './HeaderNav.vue';

  interface SiteHeaderProps {
    redirect?: boolean;
    user?: AccountUser | null;
    accountUrl?: string;
    buyVisible?: boolean;
  }

  const props = withDefaults(defineProps<SiteHeaderProps>(), {
    user: null,
    accountUrl: '',
    buyVisible: true,
  });

  interface SiteHeaderEmits {
    (e: 'login'): void;
    (e: 'logout'): void;
    (e: 'buy'): void;
  }

  const emit = defineEmits<SiteHeaderEmits>();

  const { isSmaller } = useViewport();

  const isAdaptive = isSmaller(Breakpoint.L);

  const onLogin = (): void => {
    emit('login');
  };

  const onClick = () => {
    window.location.href = '/';
  }
  const onSkolkovo = () => {
    window.open('https://sk.ru/');
  }

  const onLogout = (): void => {
    emit('logout');
  };
</script>

<template>
  <header class="site-header" data-testid="site-header">
    <div class="site-header__bar">
      <div class="site-header__left">
        <Picture
          path="travelata/logo/logo-white-short.svg"
          :lazy="false"
          :extensions="[]"
          :high-resolution="false"
          fit="contain"
          @click.capture="onClick"
          class="site-header__travelata"
        />
        <div class="site-header__left-nav-teleport" v-if="!isAdaptive"></div>

        <Teleport :to="isAdaptive ? '.site-header__bottom-nav-teleport' : '.site-header__left-nav-teleport'"> 
          <slot name="nav"></slot>
          <HeaderNav v-if="!$slots.nav" /> 
        </Teleport>
      </div>

      <div class="site-header__right">
        <Picture
          path="partner/logo/skolkovo-member-white.svg"
          :lazy="false"
          :extensions="[]"
          :high-resolution="false"
          fit="contain"
          @click.capture="onSkolkovo"
          class="site-header__skolkovo"
        />
        <div class="site-header__buttons">
          <Button
            v-if="buyVisible"
            type="button"
            class="site-header__buy-btn"
            appearance="sub-primary"
            icon-left="briefcase"
            size="s"
            @click="emit('buy')"
          >
            Купить тур в офисе
          </Button>

          <HeaderAccount
            :user="user"
            :account-url="accountUrl"
            @login="onLogin"
            @logout="onLogout"
          />
        </div>
      </div>
      <div class="site-header__bottom" v-if="isAdaptive">
        <div class="site-header__bottom-nav-teleport"></div>
        <div class="site-header__bottom-buy-teleport"></div>
      </div>
    </div>
  </header>
</template>

<style scoped lang="scss">
  .site-header {
    background: linear-gradient(262deg, var(--color-cyan-40, #2CE) -14.86%, var(--color-brand-secondary, #218BFF) 6.05%, var(--color-sky-50, #218BFF) 112.65%);
    box-sizing: border-box;
    display: flex;
    justify-content: center;
    padding: var(--spacing-s) var(--spacing-l) 0 var(--spacing-l);
    width: 100%;

    &__travelata {
      cursor: pointer;
      height: 20px;
      width: 111px;
    }

    &__buttons {
      display: flex;
      align-items: center;
      justify-content: center;
      gap: var(--spacing-s);
    }

    &__buy-btn {
      color: var(--color-neutral-0);
      border: 1px solid var(--color-neutral-0);
    }

    &__skolkovo {
      height: 24px;
      width: 74px;
      cursor: pointer;
    }

    &__bar {
      align-items: center;
      display: flex;
      gap: var(--spacing-l);
      justify-content: space-between;
      margin: 0 auto;
      max-width: $max-width-xl;
      width: 100%;
    }

    &__left {
      align-items: center;
      display: flex;
      gap: var(--spacing-xl);
    }

    &__right {
      align-items: center;
      display: flex;
      gap: var(--spacing-xxl);
    }

    @media (max-width: $breakpoint-Xl) {
      &__bar {
        max-width: $max-width-l;
      }
    }
    @media (max-width: $breakpoint-l) {
      &__bar {
        display: grid;
        grid-template-columns: 1fr auto;
        grid-template-rows: auto auto;
        align-items: center;
        column-gap: var(--spacing-l);
        row-gap: var(--spacing-m);
        max-width: $max-width-m;
      }

      &__left {
        grid-column: 1 / 2;
        grid-row: 1 / 2;
      }
      &__right {
        grid-column: 2 / 3;
        grid-row: 1 / 2;
        justify-self: end;
      }
      &__bottom {
        grid-column: 1 / -1;
        grid-row: 2 / 3;
        display: flex;
        justify-content: space-between;
        align-items: center;
      }
    }
    @media (max-width: $breakpoint-m) {
      &__bar {
        max-width: $max-width-s;
      }
    }
  }
</style>
